import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  AudioPlayer,
  type Playlist,
  useAudioPlayerCore,
} from '~/components/AudioPlayer';
import type { ProductInterface } from '~/graphql';
import { useIntl } from '~/helpers/intl';

export interface PdpAudioPlayerRef {
  togglePlay: () => void;
  isPlaying: boolean;
}

type ProductAudios = ProductInterface['audios'];

const createAudioPlaylist = (productAudios: ProductAudios): Playlist => {
  const { t } = useIntl();

  return (
    productAudios
      ?.filter((audio) => audio?.file?.uri)
      .map((audio) => ({
        title:
          audio?.name ||
          t('pricing.audioDemo.fallback', { fallback: 'Audio demo' }),
        description: audio?.description || '',
        uri: `https://izotope.com/${audio?.file?.uri}`,
      })) || []
  );
};

export function usePdpAudioPlayer(productAudios: ProductAudios) {
  const [isPlayerInitialized, setIsPlayerInitialized] = useState(false);
  const [isPlayerPlaying, setIsPlayerPlaying] = useState(false);
  const playerRef = useRef<PdpAudioPlayerRef>(null);

  const handleTogglePlayer = () => {
    if (!isPlayerInitialized) {
      setIsPlayerInitialized(true);
    }
    setTimeout(() => {
      playerRef.current?.togglePlay();
    }, 0);
  };

  const handlePlayStateChange = (isPlaying: boolean) => {
    setIsPlayerPlaying(isPlaying);
  };

  const handlePlayerClose = () => {
    setIsPlayerInitialized(false);
    setIsPlayerPlaying(false);
  };

  const playlist = createAudioPlaylist(productAudios);

  return {
    playerRef,
    isPlayerPlaying,
    isPlayerInitialized,
    handleTogglePlayer,
    handlePlayStateChange,
    handlePlayerClose,
    playlist,
  };
}

export const PdpAudioPlayer = forwardRef<
  PdpAudioPlayerRef,
  {
    playlist: Playlist;
    autoPlay?: boolean;
    onPlayStateChange?: (isPlaying: boolean) => void;
    onClose?: () => void;
  }
>(({ playlist, autoPlay = false, onPlayStateChange, onClose }, ref) => {
  const {
    audioRef,
    isPlaying,
    currentTrack,
    togglePlay,
    prevTrack,
    nextTrack,
    stopPlaylist,
    onTimeUpdate,
  } = useAudioPlayerCore(playlist, autoPlay);

  const handleClose = useCallback(() => {
    stopPlaylist();
    onClose?.();
  }, [stopPlaylist, onClose]);

  useImperativeHandle(
    ref,
    () => ({
      togglePlay,
      isPlaying,
    }),
    [togglePlay, isPlaying],
  );

  useEffect(() => {
    onPlayStateChange?.(isPlaying);
  }, [isPlaying, onPlayStateChange]);

  return (
    <>
      <audio ref={audioRef} onTimeUpdate={onTimeUpdate}>
        <track src={currentTrack?.uri} kind="captions" />
      </audio>

      {currentTrack && (
        <AudioPlayer
          closePlaylist={handleClose}
          track={currentTrack}
          togglePlay={togglePlay}
          isPlaying={isPlaying}
          playlistCount={playlist.length}
          prevTrack={prevTrack}
          nextTrack={nextTrack}
        />
      )}
    </>
  );
});

export const PdpAudioButton = ({
  title,
  isPlaying,
  onClick,
}: { title: string; isPlaying: boolean; onClick: () => void }) => {
  const { t } = useIntl();

  return (
    <button
      data-testid="pdp-audio-button"
      onClick={onClick}
      className="cursor-pointer flex items-center space-x-1 mt-2"
      id="pdp-audio-button"
    >
      <svg // TODO: someday move it to ds ECOM-372
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
      >
        <circle
          cx="20"
          cy="20"
          r="19"
          fill="currentColor"
          stroke="#161819"
          stroke-width="2"
          className={isPlaying ? 'text-grey-10' : 'text-grey-90'}
        />

        {isPlaying ? (
          <g className="text-grey-90">
            <rect
              x="15.5"
              y="13"
              width="3"
              height="14"
              fill="currentColor"
              rx="1"
            />
            <rect
              x="21.5"
              y="13"
              width="3"
              height="14"
              fill="currentColor"
              rx="1"
            />
          </g>
        ) : (
          <g className="text-grey-10" fill="currentColor">
            <path d="M20.833 26.333L15.833 23.6729H12.5V17.0068H15.833L20.833 14.667V26.333Z" />
            <path d="M23.3333 22.6666C23.9166 20.75 23.9166 20.1666 23.3333 18.3333L24.4166 17.75C25.4999 19.5833 25.4999 21.4166 24.4166 23.25L23.3333 22.6666Z" />
            <path d="M25.8333 24.9166C27.3333 21.75 27.3333 18.6666 25.8333 16C25.8333 16 26.8333 15.5 26.8333 15.4166C28.8333 18.0833 28.8333 22.3333 26.8333 25.4166L25.8333 24.9166Z" />
          </g>
        )}
      </svg>

      <span className="text-sm font-medium">
        {t('pricing.audioDemo', {
          fallback: `${title ?? ''} audio demo`,
          values: {
            title: title,
          },
        })}
      </span>
    </button>
  );
};
